import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { SessionSecurityService } from '@shared/services';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { SidenavService } from '@core/services';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class SidenavComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  // Default Menu
  navigationSideMenu = [
    { link: '/', label: 'Dashboard', path: './../assets/icons/homeWhite.png', cssClass: 'home-icon', tooltip: 'Dashboard' }
  ];

  // CEO Users Menu
  CEOUsersMenu = [
    {
      link: '/member-search', label: 'User Search', path: '/assets/icons/member_search_white.png', cssClass: 'member-icon',
      tooltip: 'User Search'
    },
    {
      link: '/approval-queue', label: 'Approval Queue', path: '../assets/icons/approval_white.png', cssClass: 'approval-icon',
      tooltip: 'Approval Queue'
    },
  ];

  // BGCA/Military alliance Users Menu
  MilitaryorBgcaMenu = [
    {
      link: '/member-search', label: 'User Search', path: '/assets/icons/member_search_white.png', cssClass: 'member-icon',
      tooltip: 'User Search'
    },
    {
      link: '/approval-queue', label: 'Approval Queue', path: '../assets/icons/approval_white.png', cssClass: 'approval-icon',
      tooltip: 'Approval Queue'
    }
  ];

  // App Owners Menu
  AppOwnerMenu = [
    {
      link: '/member-search', label: 'User Search', path: '/assets/icons/member_search_white.png', cssClass: 'member-icon',
      tooltip: 'User Search'
    }
  ];

  // Admin Users Menu
  AdminUsersMenu = [
    {
      link: '/member-search', label: 'User Search', path: '/assets/icons/member_search_white.png', cssClass: 'member-icon',
      tooltip: 'User Search'
    },
    {
      link: '/approval-queue', label: 'Approval Queue', path: '../assets/icons/approval_white.png', cssClass: 'approval-icon',
      tooltip: 'Approval Queue'
    },
    {
      link: '/application-management', label: 'Application Management', path: '/assets/icons/App_mang_white.png', cssClass: 'app-manag-icon',
      tooltip: 'Application Management'
    },
    {
      link: '/role-management', label: 'Role Management', path: '../assets/icons/RoleAssignment.png', cssClass: 'role-manag-icon',
      tooltip: 'Role Management'
    },
  ];

   // Account approver Menu
   AccountApproverMenu = [
    {
      link: '/member-search', label: 'User Search', path: '/assets/icons/member_search_white.png', cssClass: 'member-icon',
      tooltip: 'User Search'
    },
    {
      link: '/approval-queue', label: 'Approval Queue', path: '../assets/icons/approval_white.png', cssClass: 'approval-icon',
      tooltip: 'Approval Queue'
    }
  ];

  // FAQ menu
  faqSideMenu = [
    { link: '/faq', label: 'Frequently Asked Questions', path: './../assets/icons/faq.png', cssClass: 'faq-icon', tooltip: 'Frequently Asked Questions' }
  ];

  @ViewChild('sidemenu') public sidenav: MatSidenav;

  constructor(private sidenavService: SidenavService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private cdr: ChangeDetectorRef,
    private sessionSecurity: SessionSecurityService) { }

  ngOnInit(): void {
    this.sessionSecurity.setObject('false', 'UserProfileNavigation');

    // Wait for MSAL to finish processing the redirect before reading accounts.
    // On first load after login, getAllAccounts() is empty until inProgress$ emits None.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.buildNavigationMenu();
      });
  }

  private buildNavigationMenu(): void {
    if (this.authService.instance.getAllAccounts().length === 0) {
      return;
    }

    // Reset to default so this method is safe to call multiple times
    this.navigationSideMenu = [
      { link: '/', label: 'Dashboard', path: './../assets/icons/homeWhite.png', cssClass: 'home-icon', tooltip: 'Dashboard' }
    ];

    const account: any = this.authService.instance.getActiveAccount()
      ?? this.authService.instance.getAllAccounts()[0];
    if (!account) { return; }

    this.authService.instance.setActiveAccount(account);
    this.sessionSecurity.setObject(account, 'loggedUser');

    const roles = account.idTokenClaims?.['role'] ?? account.idTokenClaims?.['roles'] ?? [];
    const menuType = this.ShowApproveNavigation(roles);

    if (menuType === 'Admin') {
      this.navigationSideMenu.push(...this.AdminUsersMenu);
    } else if (menuType === 'CEOUsers') {
      this.navigationSideMenu.push(...this.CEOUsersMenu);
    } else if (menuType === 'AccountApprover') {
      this.navigationSideMenu.push(...this.AccountApproverMenu);
    } else if (menuType === 'MilitaryorBgca') {
      this.navigationSideMenu.push(...this.MilitaryorBgcaMenu);
    } else if (menuType === 'ApplicationOwner') {
      this.navigationSideMenu.push(...this.AppOwnerMenu);
    } else if (menuType === 'NationalStaff' && account.idTokenClaims?.email?.toLocaleLowerCase().endsWith('bgca.org')) {
      this.navigationSideMenu.push(...this.AppOwnerMenu);
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.sidenavService.setSideMenu(this.sidenav);
    this.sidenavService.toggle();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  // Determine the Menu based on the Roles assigned to the user
  ShowApproveNavigation(roles: string | string[]) {
    const hasRole = (r: string) => Array.isArray(roles) ? roles.includes(r) : roles.indexOf(r) > -1;

    if (hasRole('PortalAdmin')) {
      this.sessionSecurity.setObject('Admin', 'Role');
      return 'Admin';
    }
    else if (hasRole('ChiefProfessionalOfficers') || hasRole('CEODelegate') ||
      hasRole('ChiefVolunteerOfficer') || hasRole('RegionalServiceDirectors')) {
      this.sessionSecurity.setObject('CEO', 'Role');
      return 'CEOUsers';
    }
    else if (hasRole('AccountApprover')) {
      this.sessionSecurity.setObject('AccountApprover', 'Role');
      return 'AccountApprover';
    }
    else if (hasRole('MilitaryApprovingOfficial') || hasRole('BGCAAlliance')) {
      this.sessionSecurity.setObject('MAO', 'Role');
      return 'MilitaryorBgca';
    }
    else if (hasRole('ApplicationOwner') && hasRole('NationalStaff')) {
      this.sessionSecurity.setObject('AO', 'Role');
      return 'ApplicationOwner';
    }
    else if (hasRole('NationalStaff')) {
      this.sessionSecurity.setObject('NS', 'Role');
      return 'NationalStaff';
    }
    else {
      this.sessionSecurity.setObject('Other', 'Role');
      return 'ClubUsers';
    }

  }

}
